import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./StatementFormDetails.css";
import ReconciliationTable from "../ReconciliationTable/ReconciliationTable";
import moment from "moment";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { Combobox } from "../../InvoiceEditor/Components/CommonUI/Combobox/Combobox";
import { useSuppliers } from "../../InvoiceEditor/supplierList";
import SetStatus from "../../SetStatus/SetStatus";
import AlertMessage from "../../AlertMessage/AlertMessage";
import { setSelectedInvoiceData } from "../../../Reducer/SelectedInvoiceDataReducer";
import { UpdateSupplierStatement } from "../../../Services/SupplierStatementService/SupplierStatementService";
import { useUnsavedChangesGuard } from "../UnsavedChangesGuard";

interface StatementFormDetailsProps {
  mistakeReported: boolean;
  onDirtyChange: (isDirty: boolean) => void;
  onSaved: () => void;
}

const StatementFormDetails: React.FC<StatementFormDetailsProps> = ({
  mistakeReported,
  onDirtyChange,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { registerSaveHandler } = useUnsavedChangesGuard();
  const selectedInvoice = useSelector(
    (state: any) => state.selectedInvoiceData?.selectedInvoiceData,
  );
  const supplierOptions = useSuppliers();
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const getInitialForm = () => ({
    itemId: String(selectedInvoice?.invoiceId ?? "100181"),
    supplier: selectedInvoice?.supplier || "Supplier ABC",
    supplierId: selectedInvoice?.supplierId ?? null,
    date:
      selectedInvoice?.statementPeriod ??
      selectedInvoice?.invoiceDate ??
      "2025-10-27",
    outstandingBalance: String(
      selectedInvoice?.outstandingBalance ?? selectedInvoice?.totalAmount ?? "5,711.99",
    ),
    note: selectedInvoice?.note ?? selectedInvoice?.description ?? "",
  });

  const [form, setForm] = useState(getInitialForm);
  const [savedForm, setSavedForm] = useState(getInitialForm);
  const loadedInvoiceId = useRef<number | null>(
    Number(selectedInvoice?.invoiceId) || null,
  );
  const hasSavedChanges = useRef(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleSave = async (): Promise<boolean> => {
    if (isSaving) return false;

    const invoiceId = Number(selectedInvoice?.invoiceId);
    const supplierId =
      form.supplierId ??
      supplierOptions.find(
        (option) =>
          option.optionName.toLowerCase() === form.supplier.toLowerCase(),
      )?.optionId;
    const outstandingBalance = Number(form.outstandingBalance);

    if (!Number.isFinite(invoiceId) || invoiceId <= 0) {
      AlertMessage({ type: "error", text: "Invalid supplier statement." });
      return false;
    }
    const rowVersion = selectedInvoice?.rowVersion;
    if (typeof rowVersion !== "string" || rowVersion.length === 0) {
      AlertMessage({
        type: "error",
        text: "The statement version is unavailable. Reload the statement and try again.",
      });
      return false;
    }
    const newErrors: Record<string, boolean> = {};
    if (supplierId == null) newErrors.supplier = true;
    if (!form.date || !moment(form.date).isValid()) newErrors.date = true;
    if (!form.outstandingBalance || !Number.isFinite(outstandingBalance)) newErrors.outstandingBalance = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.supplier) {
        AlertMessage({ type: "error", text: "Select a valid supplier before saving." });
      } else if (newErrors.date) {
        AlertMessage({ type: "error", text: "Enter a valid invoice date before saving." });
      } else if (newErrors.outstandingBalance) {
        AlertMessage({ type: "error", text: "Enter a valid outstanding balance before saving." });
      }
      return false;
    }

    setIsSaving(true);
    try {
      const result = await UpdateSupplierStatement({
        invoiceId,
        supplierId,
        StatementPeriod: `${moment(form.date).format("YYYY-MM-DD")}T00:00:00.000Z`,
        outstandingBalance,
        description: form.note,
        rowVersion,
      });

      if (!result.success) {
        AlertMessage({ type: "error", text: result.message });
        return false;
      }

      const savedForm = { ...form, supplierId };
      hasSavedChanges.current = true;
      setSavedForm(savedForm);
      setForm(savedForm);
      dispatch(
        setSelectedInvoiceData({
          ...selectedInvoice,
          supplier: form.supplier,
          supplierId,
          statementPeriod: form.date,
          outstandingBalance,
          totalAmount: outstandingBalance,
          description: form.note,
          note: form.note,
        }),
      );
      AlertMessage({ type: "success", text: result.message });
      onSaved();
      return true;
    } catch (error) {
      console.error("Failed to save supplier statement:", error);
      AlertMessage({
        type: "error",
        text: "Failed to save supplier statement. Please try again.",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const saveHandler = () => handleSaveRef.current();
    registerSaveHandler(saveHandler);
    return () => registerSaveHandler(null);
  }, [registerSaveHandler]);

  useEffect(() => {
    const currentInvoiceId = Number(selectedInvoice?.invoiceId) || null;
    const invoiceChanged = loadedInvoiceId.current !== currentInvoiceId;

    if (invoiceChanged) {
      loadedInvoiceId.current = currentInvoiceId;
      hasSavedChanges.current = false;
    } else if (hasSavedChanges.current) {
      return;
    }

    const fresh = getInitialForm();
    setSavedForm(fresh);
    setForm(fresh);
  }, [
    selectedInvoice?.description,
    selectedInvoice?.invoiceDate,
    selectedInvoice?.invoiceId,
    selectedInvoice?.note,
    selectedInvoice?.outstandingBalance,
    selectedInvoice?.supplier,
    selectedInvoice?.supplierId,
    selectedInvoice?.statementPeriod,
    selectedInvoice?.totalAmount,
  ]);

  useEffect(
    () => () => {
      onDirtyChange(false);
    },
    [onDirtyChange],
  );

  return (
    <div className="sfd-container">
      
      <div className="sfd-ready-row">
        {mistakeReported ? (
          <span className="sfd-mistake-reported">Mistake reported</span>
        ) : (
            <SetStatus
            stageId={selectedInvoice?.stageId}
            archived={selectedInvoice?.archiveStatus}
          />
        )}
      </div>

    
      <div>
        <div className="sfd-section-title">Supplier Statement Details</div>

        <div className="sfd-field">
          <label className="sfd-label">Item ID</label>
          <input className="sfd-input" value={form.itemId} disabled />
        </div>
        <div className="sfd-field">
          <label className="sfd-label">Supplier</label>
          <Combobox
            allowAddNew={false}
            addNewType="supplier"
            name="supplier"
            label=""
            value={form.supplier}
            options={supplierOptions}
            onChange={(value, option) => {
              setForm((prev) => ({
                ...prev,
                supplier: value,
                supplierId: option?.optionId ?? null,
              }));
              setErrors((prev) => ({ ...prev, supplier: false }));
            }}
            containerStyle={{ backgroundColor: "#fff", width: "100%", maxWidth: "100%" }}
            inputStyle={{
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: "100%",
              ...(errors.supplier ? { border: "1.5px solid #ff4d4f" } : {}),
            }}
            
          />
        </div>
        <div className="sfd-field">
          <label className="sfd-label">Date</label>
          <DatePicker
            className={`sfd-input sfd-date-picker${errors.date ? " sfd-input-error" : ""}`}
            style={{ width: "100%" }}
            placeholder="DD/MM/YYYY"
            format="DD/MM/YYYY"
            value={form.date ? dayjs(form.date) : null}
            maxDate={dayjs()}
            onChange={(date) => {
              handleChange("date", date ? date.format("YYYY-MM-DD") : "");
            }}
            getPopupContainer={() => document.body}
          />
        </div>
        <div className="sfd-field">
          <label className="sfd-label">Outstanding Balance</label>
          <input className={`sfd-input${errors.outstandingBalance ? " sfd-input-error" : ""}`} value={form.outstandingBalance} onChange={(e) => handleChange("outstandingBalance", e.target.value)} />
        </div>
        <div className="sfd-field">
          <label className="sfd-label">Note</label>
          <textarea className="sfd-textarea" value={form.note} onChange={(e) => handleChange("note", e.target.value)} placeholder="Add a note..." />
        </div>
        <div className="sfd-save-row">
          <button
            className="sfd-save-btn"
            disabled={!isDirty || isSaving}
            style={{ opacity: isDirty && !isSaving ? 1 : 0.5, cursor: isDirty && !isSaving ? "pointer" : "not-allowed" }}
            onClick={() => void handleSave()}
          >
            {isSaving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      
      <ReconciliationTable items={selectedInvoice?.reconciliationItems ?? []} />
    </div>
  );
};

export default StatementFormDetails;
